import React, { useState, useEffect } from 'react';
import { AppState } from '../types';
import { Sparkles, Palette, Mic, Moon } from 'lucide-react';

interface LoaderProps {
  state: AppState;
  progress: number;
  total?: number;
  timer?: number;
  compact?: boolean;
}

const STORY_MESSAGES = [
  "Po mprehim penën magjike...",
  "Po kërkojmë fjalët e arta në bibliotekën e zanave...",
  "Po thurim fjalët me pe argjendi...",
  "Po i kërkojmë muzës të na tregojë sekretet e saj...",
  "Po mbushim fletët me pluhur yjesh...",
];

const IMAGE_MESSAGES = [
  "Po ngjyrosim ëndrrat me bojë ylberi...",
  "Po thajmë bojën e skenës me frymën e dragoit...",
  "Po fshehim thesare në çdo cep të skenës...",
  "Po i japim jetë personazheve me penelin magjik...",
  "Po shtojmë pak shkëlqim në çdo detaj...",
];

const AUDIO_MESSAGES = [
  "Po akordojmë harpat e tregimtarit...",
  "Po i mësojmë bilbilit melodinë e përrallës...",
  "Po ngrohim zërin e tregimtarit me çaj magjik...",
  "Po mbledhim jehonat e maleve për t'i dhënë thellësi zërit...",
  "Po përgatisim tregimtarin për të na çuar në një botë tjetër...",
];

const Loader: React.FC<LoaderProps> = ({ state, progress, total = 7, timer = 0, compact = false }) => {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % 5);
    }, 3000);
    return () => clearInterval(interval);
  }, [state]);

  const getMessage = () => {
    switch (state) {
      case AppState.GENERATING_STORY: 
        return STORY_MESSAGES[messageIndex];
      case AppState.GENERATING_IMAGES: 
        return `${IMAGE_MESSAGES[messageIndex]} (Skena ${progress}/${total})`;
      case AppState.GENERATING_AUDIO: 
        return AUDIO_MESSAGES[messageIndex];
      default: 
        return "Magjia po ndodh...";
    }
  };

  const getIcon = () => {
    if (state === AppState.GENERATING_STORY) return <span className="text-4xl md:text-6xl">✨</span>;
    if (state === AppState.GENERATING_IMAGES) return <span className="text-4xl md:text-6xl">🎨</span>;
    if (state === AppState.GENERATING_AUDIO) return <span className="text-4xl md:text-6xl">🎙️</span>;
    return <span className="text-4xl md:text-6xl">🌙</span>;
  };

  if (compact) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="animate-bounce">{getIcon()}</div>
            <span className="text-white font-bold text-xs md:text-base font-comic">{getMessage()}</span>
          </div>
          <span className="text-white font-bold text-[10px] font-mono bg-white/10 px-2 py-0.5 rounded-lg">{timer}s</span>
        </div>
        
        <div className="relative w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
          <div 
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-700" 
            style={{ 
              width: state === AppState.GENERATING_IMAGES 
                ? `${(progress / total) * 100}%` 
                : state === AppState.GENERATING_AUDIO ? '95%' : '15%' 
            }}
          ></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="relative mb-8">
        <div className="w-24 h-24 md:w-32 md:h-32 border-4 border-white/5 border-t-indigo-500 rounded-full animate-spin"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          {getIcon()}
        </div>
      </div>
      <h2 className="text-xl md:text-3xl font-bold text-white font-comic mb-2">{getMessage()}</h2>
      <p className="text-indigo-200/50 text-xs md:text-base">Prisni pak, magjia kërkon kohë...</p>
    </div>
  );
};

export default Loader;